import { get } from 'lodash';
import { flattenBenefits } from './schemeChoice.helpers';
import { isQuoteUnavailable, shouldShowAnnualTile, standardiseTheName, TripTypeKey } from './schemeChoice.rules';
import {IChannel} from '../../../../data/api/models';

export interface IQuoteCardVm {
    key: string;
    containerClassName: string;
    backgroundColor: string;
    title: string;
    schemeTypeLabel: string;
    gross: number;
    currency: string;
    schemeId: string;
    standardExcess: any;
    benefitsFlatten: any[];
    documents: any;
    signpost: any;
    rawQuote: any;
}

export interface IQuoteUpsellVm {
    schemeTypeLabel: string;
    gross: number;
    currency: string;
    schemeId: string;
}

export interface IQuoteColumnVm {
    key: string;
    main: IQuoteCardVm;
    upsell?: IQuoteUpsellVm;
}

/**
 * Returns tripTypes in product scheme order when possible, otherwise natural object order.
 */
export const getOrderedTripTypesList = (args: {
    product: any;
    quotesBySchemeId: any;
}): any[] => {
    const { product, quotesBySchemeId } = args;
    const schemeOrder = get(product, 'metadata.scheme_order', []);

    if (!quotesBySchemeId) { return []; }

    if (Array.isArray(schemeOrder) && schemeOrder.every((k) => quotesBySchemeId[k])) {
        return schemeOrder.map((sid: string) => quotesBySchemeId[sid]);
    }

    return Object.values(quotesBySchemeId);
};

export const buildQuoteCardVm = (args: {
    key: string;
    q: any;
    schemeTypeLabel: string;
    title: string;
    backgroundColor: string;
    containerClassName: string;
    signpost: any;
    channelKey: string;
}): IQuoteCardVm => {
    const q = args.q;

    return {
        key: args.key,
        containerClassName: args.containerClassName,
        backgroundColor: args.backgroundColor,
        title: args.title,
        schemeTypeLabel: args.schemeTypeLabel,
        gross: q && q.gross ? q.gross : 0,
        currency: q && q.currency,
        schemeId: q && q.scheme && q.scheme.id,
        standardExcess: q && q.information ? q.information.standardExcess : null,
        benefitsFlatten: flattenBenefits(q && q.benefits ? q.benefits : [], args.channelKey),
        documents: q && q.scheme ? q.scheme.documents : null,
        signpost: args.signpost,
        rawQuote: q,
    };
};

export const buildStandardQuoteColumnsVm = (args: {
    orderedTripTypes: any[];
    tripTypeValue: string;
    schemeId: string;
    quote: any; // whole quote state (for shouldShowAnnualTile)
    signpost: any;
    channel: IChannel;
    maxTripDuration: string;
}): IQuoteColumnVm[] => {
    const { orderedTripTypes, tripTypeValue, schemeId, quote, signpost, channel, maxTripDuration } = args;

    const isSingleTrip = tripTypeValue === 'single';
    const isAnnualTrip = tripTypeValue === 'annual';

    if (!orderedTripTypes || orderedTripTypes.length === 0) { return []; }

    // Gate 1: selected journey must be quoteable for ALL cover levels
    if (isSingleTrip) {
        const anySingleMissingOrUnavailable = orderedTripTypes.some((tt: any) => {
            const q = tt && tt.single;
            return !q || isQuoteUnavailable(q);
        });
        if (anySingleMissingOrUnavailable) { return []; }
    }

    if (isAnnualTrip) {
        const anyAnnualMissingOrUnavailable = orderedTripTypes.some((tt: any) => {
            const q = tt && tt.annual;
            return !q || isQuoteUnavailable(q);
        });
        if (anyAnnualMissingOrUnavailable) { return []; }
    }

    // Gate 2: upsell annual only if annual is quoteable for ALL cover levels
    const allowAnnualUpsellGlobally =
        isSingleTrip &&
        shouldShowAnnualTile(quote, maxTripDuration) &&
        orderedTripTypes.every((tt: any) => {
            const q = tt && tt.annual;
            return !!q && !isQuoteUnavailable(q);
        });

    const columns: IQuoteColumnVm[] = [];

    orderedTripTypes.forEach((tripTypes: any, level: number) => {
        if (!tripTypes) { return; }

        const mainQuote = isSingleTrip ? tripTypes.single : tripTypes.annual;
        if (!mainQuote) {
            columns.push(null as any);
            return;
        }

        const coverLevel = mainQuote && mainQuote.scheme ? mainQuote.scheme.coverLevel : '';
        const title = standardiseTheName(coverLevel);
        let backgroundColor = '#e6e6e6';
        if (coverLevel !== 'gold') {
            backgroundColor = '#FFD700';
        }

        // Standard mode has ONE container. Active if main OR upsell scheme id selected.
        const mainSelected =
            mainQuote &&
            mainQuote.scheme &&
            mainQuote.scheme.id === schemeId;

        const annualQuote = tripTypes ? tripTypes.annual : null;
        const upsellAvailable =
            allowAnnualUpsellGlobally &&
            !!annualQuote &&
            !isQuoteUnavailable(annualQuote);

        const upsellSelected =
            upsellAvailable &&
            annualQuote &&
            annualQuote.scheme &&
            annualQuote.scheme.id === schemeId;

        const containerClassName = (mainSelected || upsellSelected)
            ? 'quote-container active'
            : 'quote-container';

        const mainVm = buildQuoteCardVm({
            key: 'main-' + level,
            q: mainQuote,
            schemeTypeLabel: tripTypeValue,
            title,
            backgroundColor,
            containerClassName,
            signpost,
            channelKey: channel.key,
        });

        let upsell: IQuoteUpsellVm | undefined;

        if (upsellAvailable) {
            upsell = {
                schemeTypeLabel: 'annual',
                gross: annualQuote && annualQuote.gross ? annualQuote.gross : 0,
                currency: annualQuote && annualQuote.currency,
                schemeId: annualQuote && annualQuote.scheme && annualQuote.scheme.id,
            };
        }

        columns.push({
            key: 'col-' + level,
            main: mainVm,
            upsell,
        });
    });

    return columns;
};

export const buildSideBySideVm = (args: {
    tripTypes: any;
    tripTypeValue: string;
    schemeId: string;
    signpost: any;
    channel: IChannel;
}): IQuoteCardVm[] => {
    const { tripTypes, tripTypeValue, schemeId, signpost, channel } = args;

    const singleQuote = tripTypes && tripTypes.single;
    const annualQuote = tripTypes && tripTypes.annual;

    // Gate: selected journey must be quoteable
    if (tripTypeValue === 'single') {
        if (!singleQuote || isQuoteUnavailable(singleQuote)) { return []; }
    }

    if (tripTypeValue === 'annual') {
        if (!annualQuote || isQuoteUnavailable(annualQuote)) { return []; }
    }

    const cards: IQuoteCardVm[] = [];

    if (tripTypeValue === 'single') {
        const singleSelected =
            singleQuote &&
            singleQuote.scheme &&
            singleQuote.scheme.id === schemeId;

        cards.push(
            buildQuoteCardVm({
                key: 'sbs-single',
                q: singleQuote,
                schemeTypeLabel: 'single',
                title: standardiseTheName('single'),
                backgroundColor: '#e6e6e6',
                containerClassName: singleSelected ? 'quote-container active' : 'quote-container',
                signpost,
                channelKey: channel.key,
            }),
        );

        if (annualQuote && !isQuoteUnavailable(annualQuote)) {
            const annualSelected =
                annualQuote &&
                annualQuote.scheme &&
                annualQuote.scheme.id === schemeId;

            cards.push(
                buildQuoteCardVm({
                    key: 'sbs-annual',
                    q: annualQuote,
                    schemeTypeLabel: 'annual',
                    title: standardiseTheName('annual'),
                    backgroundColor: '#e6e6e6',
                    containerClassName: annualSelected ? 'quote-container active' : 'quote-container',
                    signpost,
                    channelKey: channel.key,
                }),
            );
        }

        return cards;
    }

    const annualSelected =
        annualQuote &&
        annualQuote.scheme &&
        annualQuote.scheme.id === schemeId;

    cards.push(
        buildQuoteCardVm({
            key: 'sbs-annual',
            q: annualQuote,
            schemeTypeLabel: 'annual',
            title: standardiseTheName('annual'),
            backgroundColor: '#e6e6e6',
            containerClassName: annualSelected ? 'quote-container active' : 'quote-container',
            signpost,
            channelKey: channel.key,
        }),
    );

    return cards;
};

export const pickBenefitsQuote = (tripTypes: any, tripType: TripTypeKey): any => {
    if (!tripTypes) { return null; }

    const preferred = tripTypes[tripType];
    if (preferred && preferred.benefits && !isQuoteUnavailable(preferred)) { return preferred; }

    const single = tripTypes.single;
    if (single && single.benefits && !isQuoteUnavailable(single)) { return single; }

    const annual = tripTypes.annual;
    if (annual && annual.benefits && !isQuoteUnavailable(annual)) { return annual; }

    return null;
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/scenes/Quote/scenes/SchemeChoiceB/schemeChoice.viewModels.ts